import { memo, useContext, useEffect, useRef, useState } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { cn } from '@/utils/tailwind';

import { StateContext } from '../../../control/state';
import { isRunExecuting } from '../../../control/state.utils';
import {
  StudioRenderMode,
  StudioRenderModeContext,
} from '../../../hooks/useStudioRenderMode';
import { useTurnsForThread } from '../../../hooks/useTurnsForThread';
import { EmptyTraceSplash } from './EmptyTraceSplash';
import GraphTurnLevelThread from './GraphTurnLevelThread';
import { InteractLogLoadingSkeleton } from './InteractLogLoadingSkeleton';
import ThreadInfoLevelSlider from './ThreadInfoLevelSlider';
import { TraceView } from './TraceView';
import { useTraceLogStore } from './store/traceLogStore';

export const GraphTrace = memo(
  (props: { selectedNodeIds: string[]; className?: string | undefined }) => {
    // note: initializes store
    useTraceLogStore();

    const state = useContext(StateContext);
    const { renderMode } = useContext(StudioRenderModeContext);
    const [peekedRunId, setPeekedRunId] = useState<string | undefined>(
      undefined
    );

    const { threadGroupedByTurns } = useTurnsForThread();
    const lastTurnRunId = threadGroupedByTurns[
      Object.keys(threadGroupedByTurns).length - 1
    ]?.find((e) => e.meta.runId)?.meta.runId;

    const firstRunId =
      state.runState.type === 'running' ? state.runState.runId : lastTurnRunId;

    useEffect(() => {
      if (firstRunId) {
        setPeekedRunId(firstRunId);
      }
    }, [firstRunId, renderMode, setPeekedRunId]);

    const divRef = useRef<HTMLDivElement>(null);

    if (state.traceError) {
      return (
        <div className="flex flex-1 flex-col p-4">
          <ExpandableErrorAlert error={state.traceError} />
        </div>
      );
    }

    const renderBody = () => {
      if (state.traceLoading) {
        return <InteractLogLoadingSkeleton />;
      }
      if (state.traceLog.length === 0 && !isRunExecuting(state.runState)) {
        return <EmptyTraceSplash />;
      }

      return <GraphTurnLevelThread />;
    };

    if (renderMode === StudioRenderMode.TRACE && peekedRunId) {
      return (
        <TraceView peekedRunId={peekedRunId} setPeekedRunId={setPeekedRunId} />
      );
    }

    return (
      <div
        ref={divRef}
        className={cn(
          props.className,
          'flex flex-1 flex-col overflow-y-auto bg-background [scrollbar-width:thin]'
        )}
        data-testid="thread-log"
      >
        <ThreadInfoLevelSlider />
        {renderBody()}
      </div>
    );
  }
);
